import { useEffect, useId, useRef, useState, type FocusEvent, type PointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from '../../../lib/i18n';
import { useProductName } from '../../../lib/use-brand';
import { useInstallQrLink } from '../../../lib/use-install-qr-link';
import { clampInlineStart } from '../../../lib/popover-placement';
import { StoreBadges } from '../catch-up/MobileAppCard';
import { Card, Typography } from '../ui';
import { HOME3_PAGE } from './Home3Feed';
import styles from './Home3MobileAppCard.module.css';

// "Download Symphony mobile app" — a full-width banner above the brief row of the Overview home
// (Figma 21849:60923): the copy on the left, both store badges on the right. The QR is not drawn in
// the banner; it appears on hover, in a small card that follows the pointer, and under the banner's
// trailing edge on keyboard focus (Figma 21849:61690, behaviour from the "Show QR Code on Hover" Make
// prototype).
//
// No native counterpart, deliberately — see `MobileAppCard` and orion-web/CLAUDE.md.

const QR_SIZE = 120;
/** The popover's outer side: the 120 code plus the card's 20 padding on each side. */
const POPOVER_SIZE = 160;
const POINTER_OFFSET = 16;
const VIEWPORT_MARGIN = 16;
const FOCUS_GAP = 12;

export function Home3MobileAppCard({ className = '' }: { className?: string } = {}) {
  const { t } = useTranslation();
  const productName = useProductName();
  const qrLink = useInstallQrLink();
  const titleId = useId();
  const popoverId = useId();
  const popoverRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  // The popover is portalled to <body>: `.root` of the home is a size container, and containment
  // makes it the containing block for `position: fixed`, so a fixed popover inside it would be
  // placed against the page column instead of the viewport.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Written straight to the element, not through state: a pointer move fires dozens of times a
  // second and re-rendering the card for each one buys nothing.
  const placeAt = (x: number, y: number) => {
    const el = popoverRef.current;
    if (!el) return;
    const left = clampInlineStart(x, POPOVER_SIZE, { width: window.innerWidth, height: window.innerHeight }, VIEWPORT_MARGIN);
    const top = Math.max(VIEWPORT_MARGIN, Math.min(y, window.innerHeight - POPOVER_SIZE - VIEWPORT_MARGIN));
    el.style.transform = `translate3d(${left}px, ${top}px, 0)`;
  };

  const cardRef = useRef<HTMLElement | null>(null);
  const placeUnderCard = () => {
    const box = cardRef.current?.getBoundingClientRect();
    if (box) placeAt(box.right - POPOVER_SIZE, box.bottom + FOCUS_GAP);
  };

  // Opened by focus, the popover hangs off the banner, so it has to follow the banner when the home
  // scrolls or the window resizes. Opened by hover, the next pointer move places it.
  useEffect(() => {
    if (!focused || hovered) return;
    window.addEventListener('scroll', placeUnderCard, { capture: true, passive: true });
    window.addEventListener('resize', placeUnderCard);
    return () => {
      window.removeEventListener('scroll', placeUnderCard, { capture: true });
      window.removeEventListener('resize', placeUnderCard);
    };
  });

  // Touch has no hover, and a tap must not leave the code stranded on screen — neither through the
  // pointer moves nor through the focus a tap on the banner or a badge gives it.
  const touchFocus = useRef(false);
  const onPointerDown = (e: PointerEvent<HTMLElement>) => {
    touchFocus.current = e.pointerType !== 'mouse';
  };

  const onPointerMove = (e: PointerEvent<HTMLElement>) => {
    if (e.pointerType !== 'mouse') return;
    placeAt(e.clientX + POINTER_OFFSET, e.clientY + POINTER_OFFSET);
    if (!hovered) setHovered(true);
  };

  const onFocus = (e: FocusEvent<HTMLElement>) => {
    if (touchFocus.current) {
      touchFocus.current = false;
      return;
    }
    cardRef.current = e.currentTarget;
    placeUnderCard();
    setFocused(true);
  };

  // Focus moving between the banner and its own badges is still focus inside the banner.
  const onBlur = (e: FocusEvent<HTMLElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false);
  };

  const open = hovered || focused;

  return (
    <Card
      component="section"
      tabIndex={0}
      aria-labelledby={titleId}
      aria-describedby={popoverId}
      className={`${styles.card} ${className}`.trim()}
      onPointerDown={onPointerDown}
      onKeyDown={() => (touchFocus.current = false)}
      onPointerMove={onPointerMove}
      onPointerLeave={() => setHovered(false)}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      <div className={styles.text}>
        <Typography id={titleId} variant="display-05" color="primary" component="h2" className={styles.title}>
          {t('home.mobileApp.title', { productName })}
        </Typography>
        <Typography variant="p3" color="secondary" component="p" className={styles.caption}>
          {t('home.mobileApp.caption')}
        </Typography>
      </div>
      <StoreBadges page={HOME3_PAGE} className={styles.badges} />
      {mounted &&
        createPortal(
          <div
            ref={popoverRef}
            id={popoverId}
            role="tooltip"
            className={`${styles.popover}${open ? ` ${styles.popoverOpen}` : ''}`}
          >
            <Card variant="shadow" className={styles.qrCard}>
              {/* Black-on-white in BOTH themes — an inverted QR is refused by many scanners. */}
              <QRCodeSVG
                value={qrLink}
                size={QR_SIZE}
                level="M"
                marginSize={0}
                bgColor="var(--ds-white)"
                fgColor="var(--ds-black)"
                title={t('home.mobileApp.qrAlt', { productName })}
              />
            </Card>
          </div>,
          document.body,
        )}
    </Card>
  );
}
